import {
  type ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'

interface TruncatedTextTooltipProps {
  children: ReactNode
  content?: ReactNode
  className?: string
  contentClassName?: string
  sideOffset?: number
  delayDuration?: number
  alwaysShow?: boolean
  closeOnPointerDown?: boolean
}

export function TruncatedTextTooltip({
  children,
  content,
  className,
  contentClassName,
  sideOffset = 8,
  delayDuration = 250,
  alwaysShow = false,
  closeOnPointerDown = false,
}: TruncatedTextTooltipProps) {
  const ref = useRef<HTMLSpanElement>(null)
  const [isOverflowing, setIsOverflowing] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const suppressOpenRef = useRef(false)

  const measure = useCallback(() => {
    const element = ref.current
    if (!element) return

    setIsOverflowing(
      element.scrollWidth > element.clientWidth ||
        element.scrollHeight > element.clientHeight
    )
  }, [])

  useLayoutEffect(() => {
    measure()
  }, [children, content, measure])

  useEffect(() => {
    const element = ref.current
    if (!element) return

    const observer =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure)

    observer?.observe(element)
    window.addEventListener('resize', measure)

    return () => {
      observer?.disconnect()
      window.removeEventListener('resize', measure)
    }
  }, [measure])

  const trigger = (
    <span
      ref={ref}
      className={cn('block min-w-0 max-w-full', className)}
      onPointerEnter={() => {
        suppressOpenRef.current = false
      }}
      onPointerDownCapture={() => {
        if (!closeOnPointerDown) return
        suppressOpenRef.current = true
        setIsOpen(false)
      }}
    >
      {children}
    </span>
  )

  if (!content || (!alwaysShow && !isOverflowing)) {
    return trigger
  }

  return (
    <TooltipProvider delayDuration={delayDuration}>
      <Tooltip
        open={closeOnPointerDown ? isOpen : undefined}
        onOpenChange={
          closeOnPointerDown
            ? nextOpen => {
                if (nextOpen && suppressOpenRef.current) return
                setIsOpen(nextOpen)
              }
            : undefined
        }
      >
        <TooltipTrigger asChild>{trigger}</TooltipTrigger>
        <TooltipContent
          side='top'
          align='center'
          sideOffset={sideOffset}
          className={contentClassName}
        >
          {content}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}
